/**
 * The brand, Funded Lime (owner, 2026-10-31, ADR-0045): a lime cut across — an acid rind and six segments of pulp,
 * one of them cobalt: the firm's share of the profit split. `LogoMark` is the sign on its midnight tile (favicon, app
 * icons, avatars); `Logo` adds the wordmark: FUNDED and the "LIME" chip. The same drawing lives in public/favicon.svg
 * and the app icons (64 × 64 grid).
 */
import { cn } from "@prop/ui";

const LIME = "#D4FF24";
const PULP = "#E9FF8A";
const COBALT = "#2F5BFF";
const INK = "#030407";
/** The six segments clockwise from twelve o'clock; the last one (eleven o'clock) is the cobalt one. */
const SEGMENTS = [
  "M33.17 15.24A16.8 16.8 0 0 1 45.93 22.61L35.73 29.48A4.5 4.5 0 0 0 32.31 27.51Z",
  "M47.1 24.64A16.8 16.8 0 0 1 47.1 39.36L36.04 33.97A4.5 4.5 0 0 0 36.04 30.03Z",
  "M45.93 41.39A16.8 16.8 0 0 1 33.17 48.76L32.31 36.49A4.5 4.5 0 0 0 35.73 34.52Z",
  "M30.83 48.76A16.8 16.8 0 0 1 18.07 41.39L28.27 34.52A4.5 4.5 0 0 0 31.69 36.49Z",
  "M16.9 39.36A16.8 16.8 0 0 1 16.9 24.64L27.96 30.03A4.5 4.5 0 0 0 27.96 33.97Z",
  "M18.07 22.61A16.8 16.8 0 0 1 30.83 15.24L31.69 27.51A4.5 4.5 0 0 0 28.27 29.48Z",
];

/** The sign on its midnight tile. */
export function LogoMark({ size = 28, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true" className={cn("shrink-0", className)}>
      <rect width="64" height="64" rx="18" fill={INK} />
      <circle cx="32" cy="32" r="23" fill={LIME} />
      <circle cx="32" cy="32" r="19" fill={INK} />
      {SEGMENTS.map((d, i) => (
        <path key={d} d={d} fill={i === SEGMENTS.length - 1 ? COBALT : PULP} />
      ))}
    </svg>
  );
}

/**
 * The sign with the wordmark. `tone` picks the "LIME" chip for the ground: "dark" (lime chip), "light" (ink chip) or
 * "auto" (follows the app theme). `compact` hides the words on a narrow screen and keeps the sign.
 */
export function Logo({ size = 28, tone = "auto", compact = false, className }: { size?: number; tone?: "auto" | "dark" | "light"; compact?: boolean; className?: string }) {
  const chip = tone === "dark" ? "bg-[#D4FF24] text-[#030407]" : tone === "light" ? "bg-[#030407] text-[#D4FF24]" : "bg-[#030407] text-[#D4FF24] dark:bg-[#D4FF24] dark:text-[#030407]";
  return (
    <span className={cn("inline-flex items-center gap-2.5", className)}>
      <LogoMark size={size} />
      <span className={cn("items-center gap-1.5 font-display font-extrabold uppercase leading-none tracking-[-0.02em] [font-stretch:125%]", compact ? "hidden sm:inline-flex" : "inline-flex")} style={{ fontSize: Math.round(size * 0.56) }}>
        Funded
        <span className={cn("inline-flex items-center rounded-[0.3em] px-[0.34em] pb-[0.16em] pt-[0.2em] text-[0.74em] leading-none tracking-[0.02em]", chip)}>Lime</span>
      </span>
    </span>
  );
}
